import { 
  getFirestore, 
  doc, 
  getDoc,
  collection,
  addDoc,
  setDoc,
  deleteDoc,
  writeBatch,
  getDocs,
  query,
  where,
  Firestore,
  DocumentReference,
  GeoPoint,
  updateDoc,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  CollectionReference
} from 'firebase/firestore';
import { CampLocation } from '@/types/campLocation';
import { processReference } from './locationVersion/referenceUtils';
import { deleteAllLocationVersions } from './locationVersionDeleteService';
import { createLocationVersion } from './locationVersionCreateService';
import { ScrapingJob } from '@/types/scrapingSpider';
import { db } from './firebaseService';
import { updateOrgCountForDeletion, updateOrgCountForOrgChange, updateOrgCountForWorkflowChange } from './orgPublishedLocationsCounter';
import { shouldForceHideFromWorkflowStatus, didForceHideChangeForWorkflowTransition } from '@/utils/workflowForceHide';
import { omitServerOwnedCampLocationFields } from '@/utils/serverOwnedCampLocationFields';
import { updateAllOffersForLocation } from './offer/offerBatchUpdateService';

// Get a single camp location by ID (directly from Firestore, ensuring we get published data)
export const getCampLocationById = async (
  db: Firestore,
  locationId: string
): Promise<CampLocation | null> => {
  try {
    console.log(`[campLocationService] Fetching location with ID: ${locationId}`);
    
    // Get the document directly from Firestore to ensure we have the published data
    const locationRef = doc(db, 'campLocations', locationId);
    const locationSnapshot = await getDoc(locationRef);
    
    if (!locationSnapshot.exists()) {
      console.log(`[campLocationService] Location not found with ID: ${locationId}`);
      return null;
    }
    
    // Convert the document to our CampLocation type
    const locationData = locationSnapshot.data() as CampLocation;
    const location: CampLocation = {
      ...locationData,
      id: locationSnapshot.id
    };
    
    console.log(`[campLocationService] Successfully fetched location: ${location.name}`, {
      visibility: location.visibility,
      isPublished: location.visibility === "v_published",
      variation0Price: location.variations?.[0]?.priceRegular
    });
    
    return location;
  } catch (error) {
    console.error('[campLocationService] Error fetching location:', error);
    throw error;
  }
};

// Function to fetch a single camp location by ID (legacy function for backward compatibility)
export const getCampLocation = async (db: Firestore, id: string): Promise<CampLocation | undefined> => {
  try {
    console.log("Fetching camp location with ID:", id);
    const docRef = doc(db, "campLocations", id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const locationData = {
        id: docSnap.id,
        ...docSnap.data(),
      } as Record<string, any>;
      
      console.log("Raw location data:", locationData);
      
      // Convert geopoint to string format if it exists
      if (locationData.address?.latLng instanceof GeoPoint) {
        locationData.address.latLng = `${locationData.address.latLng.latitude}, ${locationData.address.latLng.longitude}`;
      }
      
      // Process document references to ensure consistent format
      if (locationData.providedByOrg) {
        locationData.providedByOrg = processReference(locationData.providedByOrg);
      }
      
      if (locationData.lastEditedBy) {
        locationData.lastEditedBy = processReference(locationData.lastEditedBy);
      }
      
      // Process latestVersionId to ensure consistent format
      if (locationData.latestVersionId) {
        if (typeof locationData.latestVersionId === 'object' && locationData.latestVersionId.path) {
          // It's already a reference object
          locationData.latestVersionId = processReference(locationData.latestVersionId);
        } else if (typeof locationData.latestVersionId === 'string') {
          // It's a string ID, convert to reference format
          locationData.latestVersionId = {
            id: locationData.latestVersionId,
            path: `campLocationVersions/${locationData.latestVersionId}`
          };
        }
      }
      
      // Process publishedVersionId to ensure consistent format
      if (locationData.publishedVersionId) {
        if (typeof locationData.publishedVersionId === 'object' && locationData.publishedVersionId.path) {
          // It's already a reference object
          locationData.publishedVersionId = processReference(locationData.publishedVersionId);
        } else if (typeof locationData.publishedVersionId === 'string') {
          // It's a string ID, convert to reference format
          locationData.publishedVersionId = {
            id: locationData.publishedVersionId,
            path: `campLocationVersions/${locationData.publishedVersionId}`
          };
        }
      }
      
      // Type casting with fallback values
      const castedLocation = {
        ...locationData,
        name: locationData.name || '',
        visibility: locationData.visibility || 'v_draft',
        address: locationData.address || { country: 'USA' },
        doesWaitlist: locationData.doesWaitlist ?? null,
        doesScholarships: locationData.doesScholarships ?? null,
      } as CampLocation;
      
      console.log("Processed location data:", castedLocation);
      return castedLocation;
    } else {
      console.log("No such document!");
      return undefined;
    }
  } catch (error) {
    console.error("Error fetching document:", error);
    return undefined;
  }
};

// Function to fetch all camp locations
export const getAllCampLocations = async (db: Firestore): Promise<CampLocation[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, "campLocations"));
    const locations: CampLocation[] = [];
    querySnapshot.forEach((doc) => {
      const locationData = {
        id: doc.id,
        ...doc.data(),
      } as Record<string, any>;
      
      // Process document references to ensure consistent format
      if (locationData.providedByOrg) {
        locationData.providedByOrg = processReference(locationData.providedByOrg);
      }
      
      if (locationData.lastEditedBy) {
        locationData.lastEditedBy = processReference(locationData.lastEditedBy);
      }
      
      // Type casting with fallback values
      const castedLocation = {
        ...locationData,
        name: locationData.name || '',
        visibility: locationData.visibility || 'v_draft',
        address: locationData.address || { country: 'USA' },
        doesWaitlist: locationData.doesWaitlist ?? null,
        doesScholarships: locationData.doesScholarships ?? null,
      } as CampLocation;
      
      locations.push(castedLocation);
    });
    return locations;
  } catch (error) {
    console.error("Error fetching documents:", error);
    return [];
  }
};

// Function to fetch camp locations with optional organization filter
export const getCampLocations = async (db: Firestore, organizationId?: string | null): Promise<CampLocation[]> => {
  try {
    let locations: CampLocation[] = [];
    
    if (organizationId) {
      // If org ID is provided, fetch locations for that org
      // When querying Firestore, we must use the actual DocumentReference
      const orgRef = doc(db, "campOrganizations", organizationId);
      const q = query(collection(db, "campLocations"), where("providedByOrg", "==", orgRef));
      const querySnapshot = await getDocs(q);
      
      querySnapshot.forEach((doc) => {
        const locationData = {
          id: doc.id,
          ...doc.data(),
        } as Record<string, any>;
  
        // Process document references to ensure consistent format
        if (locationData.providedByOrg) {
          locationData.providedByOrg = processReference(locationData.providedByOrg);
        }
        
        if (locationData.lastEditedBy) {
          locationData.lastEditedBy = processReference(locationData.lastEditedBy);
        }

        // Type casting with fallback values
        const castedLocation = {
          ...locationData,
          name: locationData.name || '',
          visibility: locationData.visibility || 'v_draft',
          address: locationData.address || { country: 'USA' },
          doesWaitlist: locationData.doesWaitlist ?? null,
          doesScholarships: locationData.doesScholarships ?? null,
        } as CampLocation;
        
        locations.push(castedLocation);
      });
    } else {
      // If no org ID, fetch all locations
      locations = await getAllCampLocations(db);
    }
    
    return locations;
  } catch (error) {
    console.error("Error fetching camp locations:", error);
    return [];
  }
};

// Function to create a new camp location
export const createCampLocation = async (db: Firestore, locationData: Omit<CampLocation, 'id'>): Promise<CampLocation> => {
  try {
    console.log("Starting camp location creation with data:", locationData);
    
    // Ensure providedByOrg is properly formatted as a document reference
    if (locationData.providedByOrg && typeof locationData.providedByOrg === 'object' && 'ref' in locationData.providedByOrg) {
      locationData = {
        ...locationData,
        providedByOrg: locationData.providedByOrg.ref
      };
    }

    // Add the new document to the 'campLocations' collection
    // Default forceHide true for new drafts (hidden from consumer search until workflow/visibility moves external)
    const docRef = await addDoc(collection(db, 'campLocations'), omitServerOwnedCampLocationFields({
      ...locationData,
      forceHide: locationData.forceHide ?? true,
      workflowStatus: (locationData as any).workflowStatus ?? 'wf_new'
    }));
    const newLocationId = docRef.id;
    console.log(`Created new location with ID: ${newLocationId}`);

    // Create a new object with the ID included
    // Ensure all required CampLocation fields are included
    const newLocationData: CampLocation = { 
      ...locationData as any, // Use 'as any' to avoid TypeScript errors with partial data
      id: newLocationId,
      visibility: locationData.visibility || 'v_draft', // Ensure visibility is set for new locations
      forceHide: (locationData as any).forceHide ?? true,
      workflowStatus: (locationData as any).workflowStatus ?? 'wf_new'
    };

    // Update the document with its own ID
    await setDoc(doc(db, 'campLocations', newLocationId), { id: newLocationId }, { merge: true });
    console.log(`Updated location ${newLocationId} with its own ID`);

    // Create initial version record with "create" action
    try {
      const versionId = await createLocationVersion(
        db,
        newLocationData,
        locationData.lastEditedBy?.id || '',
        'create',
        'Initial creation of location',
        'draft'
      );
      console.log(`Created initial version ${versionId} for location ${newLocationId}`);
    } catch (versionError) {
      console.error("Error creating initial version:", versionError);
      // We don't throw here since the location was created successfully
    }

    return newLocationData;
  } catch (error) {
    console.error("Error creating camp location:", error);
    throw error;
  }
};

// Update location data
export const updateCampLocation = async (
  db: Firestore, 
  locationId: string, 
  updateData: Partial<CampLocation>, 
  userId: string, 
  changeDescription: string = 'Updated location',
  forceUpdateLatestDraft: boolean = false,
  skipVersion: boolean = false
): Promise<CampLocation> => {
  try {
    console.log(`Updating location ${locationId} with data:`, updateData, `forceUpdateLatestDraft: ${forceUpdateLatestDraft}`);
    
    // Get current location data
    const currentLocation = await getCampLocationById(db, locationId);
    if (!currentLocation) {
      throw new Error(`Location not found: ${locationId}`);
    }
    const oldWorkflowStatus = (currentLocation as any).workflowStatus || null;
    const oldProvided = (currentLocation as any).providedByOrg as any;
    const oldOrgId = typeof oldProvided === 'string' ? oldProvided : (oldProvided && (oldProvided as any).id) ? (oldProvided as any).id : null;

    if (Object.prototype.hasOwnProperty.call(updateData, 'workflowStatus')) {
      (updateData as any).forceHide = shouldForceHideFromWorkflowStatus((updateData as any).workflowStatus);
    }

    // Update location in Firestore
    const locationRef = doc(db, 'campLocations', locationId);
    await setDoc(locationRef, omitServerOwnedCampLocationFields({ ...updateData }), { merge: true });
    
    // Create the updated location object
    const updatedLocation = {
      ...currentLocation,
      ...updateData
    };

    // After write: check for workflowStatus change and adjust org counter / offers when visibility flips
    if (Object.prototype.hasOwnProperty.call(updateData, 'workflowStatus')) {
      const newWorkflowStatus = (updateData as any).workflowStatus || null;
      if (newWorkflowStatus !== oldWorkflowStatus) {
        await updateOrgCountForWorkflowChange(db, locationId, oldWorkflowStatus, newWorkflowStatus);
      }
      if (didForceHideChangeForWorkflowTransition(oldWorkflowStatus, newWorkflowStatus)) {
        await updateAllOffersForLocation(db, locationId, userId);
      }
    }

    // After write: check for org disassociation/association changes
    if (Object.prototype.hasOwnProperty.call(updateData, 'providedByOrg')) {
      const provided = (updateData as any).providedByOrg as any;
      const newOrgId = provided == null
        ? null
        : (typeof provided === 'string' ? provided : ((provided as any).id || null));
      if (newOrgId !== oldOrgId) {
        await updateOrgCountForOrgChange(db, locationId, oldOrgId, newOrgId);
      }
    }
    
    // Create a new version record unless explicitly skipped (for non-versioned updates)
    try {
      if (!skipVersion) {
        const userRef = doc(db, "users", userId);
        const versionId = await createLocationVersion(
          db,
          updatedLocation,
          userId,
          'update',
          changeDescription,
          'draft'
        );
        console.log(`Created version ${versionId} for updated location ${locationId}`);
      } else {
        console.log(`[campLocationService] Skipping version creation for non-versioned update of ${locationId}`);
      }
    } catch (versionError) {
      console.error(`Error creating version for location update ${locationId}:`, versionError);
      // Continue without throwing since the location was updated successfully
    }
    
    return updatedLocation;
  } catch (error) {
    console.error(`Error updating location ${locationId}:`, error);
    throw error;
  }
};

/**
 * Delete a single camp location
 * @param db Firestore database instance
 * @param locationId ID of the location to delete
 * @param userId ID of the user performing the deletion
 */
export const deleteCampLocation = async (db: Firestore, locationId: string, userId?: string) => {
  try {
    console.log(`[LOCATION DELETE] Starting deletion process for location ${locationId}`);
    
    // First verify the location exists
    const locationRef = doc(db, "campLocations", locationId);
    const locationDoc = await getDoc(locationRef);
    
    if (!locationDoc.exists()) {
      console.warn(`[LOCATION DELETE] Location ${locationId} not found - nothing to delete`);
      return true;
    }
    
    // Get location data for logging
    const locationData = locationDoc.data();
    console.log(`[LOCATION DELETE] Found location to delete: ${locationId} (${locationData.name})`);

    // Adjust org counter if this location currently qualifies
    await updateOrgCountForDeletion(db, locationId);
    
    // Step 1: Delete all version records for this location
    console.log(`[LOCATION DELETE] Step 1: Deleting all version history for location ${locationId}`);
    try {
      await deleteAllLocationVersions(db, locationId);
      console.log(`[LOCATION DELETE] Successfully deleted version history for location ${locationId}`);
    } catch (versionError) {
      console.error(`[LOCATION DELETE] Failed to delete version history for location ${locationId}:`, versionError);
      throw versionError; // Stop the process if we can't delete versions
    }
    
    // Step 2: Delete the location document
    console.log(`[LOCATION DELETE] Step 2: Deleting location document ${locationId}`);
    await deleteDoc(locationRef);
    console.log(`[LOCATION DELETE] Location document ${locationId} deleted successfully`);
    
    console.log(`[LOCATION DELETE] Camp location ${locationId} and version history deleted successfully`);
    return true;
  } catch (error) {
    console.error(`[LOCATION DELETE] Error deleting camp location ${locationId}:`, error);
    throw error;
  }
};

/**
 * Check whether a location has any REAL (non-placeholder) offers.
 *
 * Placeholder offers (`isPlaceholder === true` — the year-3008 Algolia
 * discoverability seeds, one per active variation) are auto-created and
 * must NOT block a delete. A true v1 shell has only placeholder offers,
 * or none. Used as a defense-in-depth guard before deleting a location:
 * `deleteCampLocation` cascades version history but NOT offers, so we
 * refuse to orphan real offers.
 *
 * `locationRef` on `offers` is a DocumentReference, not a string, so we
 * query by the campLocations doc ref. We filter `isPlaceholder` in the
 * client (a Firestore `!=` query would silently drop docs missing the
 * field); the offer count for a single location is small.
 *
 * @param db Firestore database instance
 * @param locationId ID of the location to check
 * @returns true if at least one non-placeholder offer references this location
 */
export const locationHasNonPlaceholderOffers = async (
  db: Firestore,
  locationId: string
): Promise<boolean> => {
  const locationRef = doc(db, "campLocations", locationId);
  const offersQuery = query(
    collection(db, "offers"),
    where("locationRef", "==", locationRef)
  );
  const snapshot = await getDocs(offersQuery);
  return snapshot.docs.some(d => d.data().isPlaceholder !== true);
};

/**
 * Delete multiple camp locations in a batch operation
 * @param db Firestore database instance
 * @param locationIds Array of location IDs to delete
 * @param userId ID of the user performing the deletion
 */
export const deleteCampLocations = async (db: Firestore, locationIds: string[], userId?: string) => {
  if (!locationIds.length) return;

  try {
    console.log(`[LOCATION DELETE] Starting batch deletion for ${locationIds.length} locations: ${locationIds.join(', ')}`);
    
    // Step 1: Delete all version records for each location
    console.log(`[LOCATION DELETE] Step 1: Deleting version history for ${locationIds.length} locations`);
    let successfulVersionDeletions = 0;
    let failedVersionDeletions = 0;
    
    // Pre-adjust org counters for any qualifying locations
    for (const id of locationIds) {
      try {
        await updateOrgCountForDeletion(db, id);
      } catch (e) {
        console.error(`[LOCATION DELETE] Failed to adjust org counter before delete for ${id}:`, e);
      }
    }

    for (const id of locationIds) {
      try {
        await deleteAllLocationVersions(db, id);
        successfulVersionDeletions++;
        console.log(`[LOCATION DELETE] Successfully deleted version history for location ${id} (${successfulVersionDeletions}/${locationIds.length})`);
      } catch (versionError) {
        failedVersionDeletions++;
        console.error(`[LOCATION DELETE] Failed to delete version history for location ${id}:`, versionError);
        // Continue with other locations even if one fails
      }
    }
    
    console.log(`[LOCATION DELETE] Version deletion summary: ${successfulVersionDeletions} successful, ${failedVersionDeletions} failed`);
    
    // Step 2: Perform batch delete for all locations
    console.log(`[LOCATION DELETE] Step 2: Deleting ${locationIds.length} location documents`);
    const batch = writeBatch(db);

    locationIds.forEach(id => {
      const locationRef = doc(db, "campLocations", id);
      batch.delete(locationRef);
      console.log(`[LOCATION DELETE] Added location ${id} to batch delete operation`);
    });

    await batch.commit();
    console.log(`[LOCATION DELETE] Batch commit successful: ${locationIds.length} camp locations deleted`);

    console.log(`[LOCATION DELETE] ${locationIds.length} camp locations and their version histories deleted successfully`);
    return true;
  } catch (error) {
    console.error(`[LOCATION DELETE] Error batch deleting camp locations:`, error);
    throw error;
  }
};

/**
 * Generic service for managing camp location data across different modes
 * Supports both 'fetch' (data extraction) and 'monitor' (change detection) systems
 */

export type CampLocationMode = 'fetch' | 'monitor';

export interface ModeConfig {
  mode: CampLocationMode;
  locationId: string;
}

export interface MonitoringConfig {
  monitoringUrls: Array<{
    url: string;
    label: string;
    isActive: boolean;
    changeThreshold: number; // 0.1 = 10% change triggers alert
    lastChecked?: Timestamp;
    lastSignificantChange?: Timestamp;
  }>;
  alertSettings: {
    bigChanges: boolean;
    newCampNotices: boolean;
    priceChanges: boolean;
    emailNotifications: boolean;
    notificationEmail?: string;
  };
  checkFrequency: 'daily' | 'weekly' | 'manual';
  isActive: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  contentExtractionPatterns?: Array<{
    pattern: string;
    description?: string;
    createdAt: Timestamp;
    confidence?: number;
    avgContentScore?: number;
  }>;
}

export interface FetchConfig {
  systemPrompts: {
    [key: string]: string;
  };
  htmlWrapperPatterns: {
    [key: string]: any;
  };
  contentExtractionPatterns?: Array<{
    pattern: string;
    description?: string;
    createdAt: Timestamp;
    confidence?: number;
    avgContentScore?: number;
  }>;
  processingInstructions: string;
  maxPages: number;
  maxMinutes: number;
  concurrency: number;
  notificationEmail: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface ChangeAlert {
  id: string;
  type: 'big_diff' | 'new_camp_notice' | 'price_change' | 'content_addition' | 'content_removal';
  url: string;
  message: string;
  changePercentage?: number;
  detectedAt: Timestamp;
  jobId: string;
  contentId: string;
  acknowledged: boolean;
  acknowledgedAt?: Timestamp;
  acknowledgedBy?: string;
}

/**
 * Generic service class for camp location data management
 */
export class CampLocationService {
  
  // =====================================================================================
  //  DOCUMENT REFERENCE HELPERS
  // =====================================================================================
  
  /**
   * Get configuration document reference for a specific mode
   */
  static getConfigRef(locationId: string, mode: CampLocationMode): DocumentReference {
    if (!locationId) {
      throw new Error(`[CampLocationService] getConfigRef called with empty locationId!`);
    }
    return doc(db, 'campLocations', locationId, mode, '_config');
  }
  
  /**
   * Get jobs collection reference for a specific mode
   */
  static getJobsCollection(locationId: string, mode: CampLocationMode): CollectionReference {
    return collection(db, 'campLocations', locationId, mode);
  }
  
  /**
   * Get specific job document reference
   */
  static getJobRef(locationId: string, mode: CampLocationMode, jobId: string): DocumentReference {
    return doc(db, 'campLocations', locationId, mode, jobId);
  }
  
  /**
   * Get content document reference
   */
  static getContentRef(locationId: string, mode: CampLocationMode, contentId: string): DocumentReference {
    return doc(db, 'campLocations', locationId, mode, `content_${contentId}`);
  }
  
  /**
   * Get alerts collection reference (monitor mode only). `_alerts` is only a parent id: a collection
   * path needs an odd number of segments, so `monitor/alerts` cannot be one. Same path the removed
   * scheduled monitor (KB-4789) stored alerts at.
   */
  static getAlertsCollection(locationId: string): CollectionReference {
    return collection(db, 'campLocations', locationId, 'monitor', '_alerts', 'alerts');
  }

  /**
   * Get alert document reference (monitor mode only)
   */
  static getAlertRef(locationId: string, alertId: string): DocumentReference {
    return doc(db, 'campLocations', locationId, 'monitor', '_alerts', 'alerts', alertId);
  }

  // =====================================================================================
  //  CONFIGURATION MANAGEMENT
  // =====================================================================================
  
  /**
   * Get configuration for a specific mode
   */
  static async getConfig<T = FetchConfig | MonitoringConfig>(
    locationId: string, 
    mode: CampLocationMode
  ): Promise<T | null> {
    try {
      const configRef = this.getConfigRef(locationId, mode);
      const configDoc = await getDoc(configRef);
      
      if (!configDoc.exists()) {
        console.log(`[CampLocationService] No ${mode} config found for location: ${locationId}`);
        return null;
      }
      
      return configDoc.data() as T;
    } catch (error) {
      console.error(`[CampLocationService] Error getting ${mode} config for ${locationId}:`, error);
      throw error;
    }
  }
  
  /**
   * Set configuration for a specific mode
   */
  static async setConfig<T = FetchConfig | MonitoringConfig>(
    locationId: string, 
    mode: CampLocationMode, 
    config: T
  ): Promise<void> {
    try {
      const configRef = this.getConfigRef(locationId, mode);
      const timestamp = Timestamp.now();
      
      const configWithTimestamp = {
        ...config,
        updatedAt: timestamp,
        // Add createdAt if it doesn't exist
        ...(!(config as any).createdAt && { createdAt: timestamp })
      };
      
      await setDoc(configRef, configWithTimestamp, { merge: true });
      console.log(`[CampLocationService] ${mode} config saved for location: ${locationId}`);
    } catch (error) {
      console.error(`[CampLocationService] Error setting ${mode} config for ${locationId}:`, error);
      throw error;
    }
  }
  
  /**
   * Update specific config fields
   */
  static async updateConfig(
    locationId: string, 
    mode: CampLocationMode, 
    updates: Partial<FetchConfig | MonitoringConfig>
  ): Promise<void> {
    try {
      console.log(`[CampLocationService] Starting updateConfig for ${mode} mode:`, {
        locationId,
        mode,
        updateFields: Object.keys(updates)
      });
      
      const configRef = this.getConfigRef(locationId, mode);
      console.log(`[CampLocationService] Config document path: ${configRef.path}`);
      
      // First check if document exists
      const configDoc = await getDoc(configRef);
      console.log(`[CampLocationService] Current config exists: ${configDoc.exists()}`);
      if (configDoc.exists()) {
        console.log(`[CampLocationService] Current config data:`, configDoc.data());
      }
      
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now()
      };
      
      console.log(`[CampLocationService] Updating config with data:`, updateData);
      
      // If document doesn't exist, we need to create it with initial data
      if (!configDoc.exists()) {
        console.log(`[CampLocationService] Config document doesn't exist, creating new one...`);
        await setDoc(configRef, {
          ...updateData,
          createdAt: Timestamp.now()
        });
      } else {
        // Update existing document
        console.log(`[CampLocationService] Updating existing config document...`);
        await updateDoc(configRef, updateData);
      }
      
      // Verify the update
      const updatedDoc = await getDoc(configRef);
      console.log(`[CampLocationService] Config after update:`, updatedDoc.data());
      
      console.log(`[CampLocationService] ${mode} config updated for location: ${locationId}`);
    } catch (error) {
      console.error(`[CampLocationService] Error updating ${mode} config for ${locationId}:`, error);
      throw error;
    }
  }

  // =====================================================================================
  //  JOB MANAGEMENT
  // =====================================================================================
  
  /**
   * Create a new job for a specific mode
   */
  static async createJob(
    locationId: string, 
    mode: CampLocationMode, 
    jobData: Omit<ScrapingJob, 'id' | 'createdAt'>
  ): Promise<string> {
    try {
      const jobsCollection = this.getJobsCollection(locationId, mode);
      const timestamp = Timestamp.now();
      
      const completeJobData = {
        ...jobData,
        mode, // Ensure mode is set
        createdAt: timestamp,
        lastActivityAt: timestamp
      };
      
      const docRef = await addDoc(jobsCollection, completeJobData);
      console.log(`[CampLocationService] ${mode} job created: ${docRef.id} for location: ${locationId}`);
      return docRef.id;
    } catch (error) {
      console.error(`[CampLocationService] Error creating ${mode} job for ${locationId}:`, error);
      throw error;
    }
  }
  
  /**
   * Get a specific job
   */
  static async getJob(locationId: string, mode: CampLocationMode, jobId: string): Promise<ScrapingJob | null> {
    try {
      const jobRef = this.getJobRef(locationId, mode, jobId);
      const jobDoc = await getDoc(jobRef);
      
      if (!jobDoc.exists()) {
        return null;
      }
      
      return { id: jobDoc.id, ...jobDoc.data() } as ScrapingJob;
    } catch (error) {
      console.error(`[CampLocationService] Error getting ${mode} job ${jobId} for ${locationId}:`, error);
      throw error;
    }
  }
  
  /**
   * Update job status and progress
   */
  static async updateJob(
    locationId: string, 
    mode: CampLocationMode, 
    jobId: string, 
    updates: Partial<ScrapingJob>
  ): Promise<void> {
    try {
      const jobRef = this.getJobRef(locationId, mode, jobId);
      const updateData = {
        ...updates,
        lastActivityAt: Timestamp.now()
      };
      
      await updateDoc(jobRef, updateData);
      console.log(`[CampLocationService] ${mode} job ${jobId} updated for location: ${locationId}`);
    } catch (error) {
      console.error(`[CampLocationService] Error updating ${mode} job ${jobId} for ${locationId}:`, error);
      throw error;
    }
  }
  
  /**
   * Get recent jobs for a specific mode
   */
  static async getRecentJobs(
    locationId: string, 
    mode: CampLocationMode, 
    limitCount: number = 10
  ): Promise<ScrapingJob[]> {
    try {
      const jobsCollection = this.getJobsCollection(locationId, mode);
      const q = query(
        jobsCollection,
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ScrapingJob));
    } catch (error) {
      console.error(`[CampLocationService] Error getting recent ${mode} jobs for ${locationId}:`, error);
      throw error;
    }
  }
  
  /**
   * Listen to job changes in real-time
   */
  static subscribeToJobs(
    locationId: string, 
    mode: CampLocationMode, 
    callback: (jobs: ScrapingJob[]) => void,
    limitCount: number = 20
  ): () => void {
    const jobsCollection = this.getJobsCollection(locationId, mode);
    const q = query(
      jobsCollection,
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    return onSnapshot(q, (snapshot) => {
      const jobs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ScrapingJob));
      callback(jobs);
    }, (error) => {
      console.error(`[CampLocationService] Error in ${mode} jobs subscription for ${locationId}:`, error);
    });
  }

  // =====================================================================================
  //  MONITORING-SPECIFIC METHODS
  // =====================================================================================
  
  /**
   * Create a change alert (monitor mode only)
   */
  static async createAlert(locationId: string, alertData: Omit<ChangeAlert, 'id' | 'detectedAt'>): Promise<string> {
    try {
      const alertsCollection = this.getAlertsCollection(locationId);
      const timestamp = Timestamp.now();
      
      const completeAlertData = {
        ...alertData,
        detectedAt: timestamp,
        acknowledged: false
      };
      
      const docRef = await addDoc(alertsCollection, completeAlertData);
      console.log(`[CampLocationService] Alert created: ${docRef.id} for location: ${locationId}`);
      return docRef.id;
    } catch (error) {
      console.error(`[CampLocationService] Error creating alert for ${locationId}:`, error);
      throw error;
    }
  }
  
  /**
   * Get recent alerts for a location
   */
  static async getRecentAlerts(locationId: string, limitCount: number = 10): Promise<ChangeAlert[]> {
    try {
      const alertsCollection = this.getAlertsCollection(locationId);
      const q = query(
        alertsCollection,
        orderBy('detectedAt', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ChangeAlert));
    } catch (error) {
      console.error(`[CampLocationService] Error getting alerts for ${locationId}:`, error);
      throw error;
    }
  }
  
  /**
   * Acknowledge an alert
   */
  static async acknowledgeAlert(locationId: string, alertId: string, userId: string): Promise<void> {
    try {
      const alertRef = this.getAlertRef(locationId, alertId);
      await updateDoc(alertRef, {
        acknowledged: true,
        acknowledgedAt: Timestamp.now(),
        acknowledgedBy: userId
      });
      console.log(`[CampLocationService] Alert ${alertId} acknowledged by ${userId}`);
    } catch (error) {
      console.error(`[CampLocationService] Error acknowledging alert ${alertId}:`, error);
      throw error;
    }
  }

  // =====================================================================================
  //  UTILITY METHODS
  // =====================================================================================
  
  /**
   * Check if a location has configuration for a specific mode
   */
  static async hasConfig(locationId: string, mode: CampLocationMode): Promise<boolean> {
    try {
      const configRef = this.getConfigRef(locationId, mode);
      const configDoc = await getDoc(configRef);
      return configDoc.exists();
    } catch (error) {
      console.error(`[CampLocationService] Error checking ${mode} config for ${locationId}:`, error);
      return false;
    }
  }
  
  /**
   * Get monitoring status for a location
   */
  static async getMonitoringStatus(locationId: string): Promise<{
    isConfigured: boolean;
    isActive: boolean;
    lastAlert?: ChangeAlert;
    urlCount: number;
  }> {
    try {
      const config = await this.getConfig<MonitoringConfig>(locationId, 'monitor');
      
      if (!config) {
        return {
          isConfigured: false,
          isActive: false,
          urlCount: 0
        };
      }
      
      const alerts = await this.getRecentAlerts(locationId, 1);
      const lastAlert = alerts.length > 0 ? alerts[0] : undefined;
      
      return {
        isConfigured: true,
        isActive: config.isActive,
        lastAlert,
        urlCount: config.monitoringUrls?.length || 0
      };
    } catch (error) {
      console.error(`[CampLocationService] Error getting monitoring status for ${locationId}:`, error);
      return {
        isConfigured: false,
        isActive: false,
        urlCount: 0
      };
    }
  }
  
  /**
   * Initialize default monitoring config for a location
   */
  static async initializeMonitoringConfig(
    locationId: string, 
    initialUrls: Array<{ url: string; label: string }> = []
  ): Promise<void> {
    const defaultConfig: MonitoringConfig = {
      monitoringUrls: initialUrls.map(({ url, label }) => ({
        url,
        label,
        isActive: true,
        changeThreshold: 0.1 // 10% change threshold
      })),
      alertSettings: {
        bigChanges: true,
        newCampNotices: true,
        priceChanges: true,
        emailNotifications: false
      },
      checkFrequency: 'daily',
      isActive: initialUrls.length > 0,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    
    await this.setConfig(locationId, 'monitor', defaultConfig);
  }
}

export default CampLocationService;
